<template>
  <section class="interesting constructor_section">
    <ConstructorComponentsSectionHeader :title="data.title" class="header">
      <ui-horizontal-scroll class="filters">
        <ui-ui-button
          v-for="(filter, index) in data.variants"
          :key="index"
          :type="index === activeFilter ? 'primary' : 'secondary'"
          size="medium"
          class="filter"
          font-class="body-m-regular"
          @click="
            pickVariant(index);
            sendYa(data.title, filter);
          "
        >
          {{ filter }}
        </ui-ui-button>
      </ui-horizontal-scroll>
    </ConstructorComponentsSectionHeader>

    <div ref="card" class="items">
      <template v-if="loading">
        <div
          v-for="item in savedVariants[activeFilter]"
          :key="item.id"
          class="item item__skeleton"
        />
      </template>

      <template v-else>
        <div
          v-for="(item, index) in savedVariants[activeFilter]"
          :key="item.id"
          class="link-wrap"
        >
          <card-offer-tile
            v-if="item.type == 'offer'"
            :data-slot="index + 1"
            :data-url="objToBase64({ url: item.view_url })"
            :data-offer_id="item.id"
            data-block="carousel"
            :model="isMobile ? { ...item, additional_images: [] } : item"
            type-style="tile"
            :type="item.type"
            class="card"
            viewed
            :position="index + 1"
            block-type="model-similar"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import type {
  OfferListBlockContent,
  OfferListBlockContentItems,
} from '~/interfaces/constructor';
import { useGlobalStore } from '~/stores/global';
import { useAnalyticsStore } from '~/stores/analytics';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import { _getConstructorData } from '~/api/shopOffers';
import cardOfferTile from '~/components/product-card/productCardTileOffer/productCardTileOffer.vue';
import { objToBase64 } from '~/utils/base64';

defineEmits(['openModal', 'sendAnalytics']);
const props = defineProps({
  data: {
    type: Object as () => OfferListBlockContent,
    required: true,
  },
  white_buttons: {
    type: Boolean,
    default: false,
  },
});

const globalStore = useGlobalStore();
const analyticsStore = useAnalyticsStore();
const mediaStore = useMediaQueryStore();
const { $ymAnyEvent } = useNuxtApp();
const isMobile = computed(
  () =>
    mediaStore.isMobile || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);
onMounted(() => {
  nextTick(() => {
    observeElements();
  });
});

const loading = ref<Boolean>(false);
const activeFilter = ref<Number>(0);
const savedVariants = ref<OfferListBlockContentItems[][]>(
  props.data?.variants?.length
    ? (() => {
        const arr = [...Array(props.data.variants.length).fill(null)];
        arr[props.data?.defaultVariant || 0] = props.data.items;
        return arr;
      })()
    : [props.data.items]
);
const card = ref<HTMLElement | undefined>();

const sendYa = (title: string, filter: string): void => {
  if (title && /Топ скидок/i.test(title)) {
    const diap: string = filter.replace(/\D/g, '');
    $ymAnyEvent('reachGoal', `click_${diap}`);
  }
};

function pickVariant(index: number) {
  if (savedVariants.value[index]) {
    activeFilter.value = index;
    return;
  }
  observeElements(true);
  loading.value = true;
  _getConstructorData(`/blocks/${props.data.id}/content`, {
    params: {
      variant: index,
      region_id: globalStore.region.id,
      device: globalStore.device || 1,
      sem: globalStore.sem,
    },
  })
    .then((res) => {
      savedVariants.value = [
        ...savedVariants.value.slice(0, index),
        res.items,
        ...savedVariants.value.slice(index + 1),
      ];
      activeFilter.value = index;
      setTimeout(() => {
        observeElements();
      }, 200);
    })
    .catch(() => {})
    .finally(() => (loading.value = false));
}

onMounted(() => {
  nextTick(() => {
    observeElements();
    if (props.data?.defaultVariant !== undefined) {
      pickVariant(props.data.defaultVariant);
    }
  });
});

onBeforeUnmount(() => {
  observeElements(true);
});

function observeElements(unobserve = false) {
  const list: NodeListOf<HTMLElement> | undefined =
    card.value?.querySelectorAll('.item:not(.item__skeleton)');
  if (list?.length && list.forEach instanceof Function) {
    list.forEach((e: HTMLElement) => {
      unobserve
        ? analyticsStore.unobserveView(e)
        : analyticsStore.observeView(e);
    });
  }
}
</script>
<style lang="scss" scoped src="./CustomScss.scss"></style>
